import { z } from 'zod';
import { BoundingBox, Confidence, Id, Rupiah, Timestamp } from './primitives.js';
import { SkuDivision, SkuType } from './master.js';
import { PhotoType } from './photo.js';
import { PlanogramGrade, PlanogramRank } from './planogram.js';
/**
 * FR-10 Indonesian price formats.
 *
 * Recorded alongside the normalised value so the golden-set harness can report
 * accuracy per format, and so a correction tells us which format the model
 * misread — that is the tuning signal, not the raw miss count.
 *
 * SHORTHAND IS THE ONE INFERRED FORMAT. Every other value describes digits that
 * were actually on the tag; SHORTHAND says the parser supplied three zeroes the
 * tag did not show, because a warung price written "25" means 25.000 and there
 * is no such thing as a 25 rupiah product. It is labelled separately precisely
 * because it is a guess: it is the one format whose accuracy has to be watched,
 * and the one a correction can tell us to stop making.
 */
export const PriceFormat = z.enum([
    'RP_FULL', //      Rp 20.000
    'PLAIN', //        1.200.000
    'K_SUFFIX', //     20K
    'RB_SUFFIX', //    20rb
    'JT_SUFFIX', //    1,2jt
    'SHORTHAND', //    25 , meaning 25.000
    'UNKNOWN',
]);
/** FR-10: a tag may show one price, a range, or a promo strikethrough pair. */
export const PriceKind = z.enum(['SINGLE', 'RANGE', 'PROMO']);
/**
 * One price reading from one tag or menu line.
 *
 * `rawText` is always retained even when normalisation fails, because FR-06
 * lets the user correct an unreadable price by hand and the pair
 * (rawText, corrected value) is exactly the FR-10 training signal.
 */
export const PriceObservation = z.object({
    rawText: z.string().max(200),
    kind: PriceKind,
    format: PriceFormat,
    /** Null when OCR produced text that could not be normalised to a number. */
    valueIdr: Rupiah.nullable(),
    /** Upper bound when kind is RANGE (e.g. 20K–25K). */
    rangeMaxIdr: Rupiah.nullable(),
    /** The struck-through original when kind is PROMO. `valueIdr` holds the new price. */
    strikethroughIdr: Rupiah.nullable(),
    /** FR-10 requires both printed and handwritten support; they have different thresholds (TSP 3.3). */
    handwritten: z.boolean(),
    confidence: Confidence,
});
/**
 * Where a reading's facings, rows and sequence came from — ADR-0013.
 *
 * `MODEL` is the cloud model asserting them alongside its `bbox`. `DETECTOR` is
 * arithmetic over a detector's boxes: facings counted, rows clustered, sequence
 * ranked.
 *
 * ON THE CONTRACT BECAUSE THE DEGRADATION IS SILENT. Detection deliberately does
 * NOT fail closed the way the face blur does — a missing detector falls back to
 * MODEL and the photograph is still read, because losing an hour of better
 * counting is recoverable (ADR-0013 decision 5). The cost of that choice is that
 * a detector which quietly stops loading looks exactly like one that is working.
 * This field is what makes the difference visible: the alert is the proportion of
 * MODEL readings, which since scoring v4 has no toggle to be excused by.
 *
 * IT IS ALSO A SCORING INPUT NOW. Per-facing sequence grading needs real box
 * positions, so `PlanogramScore` stamps this value: `DETECTOR` grades every
 * facing and takes the majority, `MODEL` grades the one stored position.
 *
 * NOT NULLABLE, for the reason `tier` is not. A FAILED analysis reports the path
 * that was ATTEMPTED rather than one that produced anything — the alternative is
 * a null that means both "nothing ran" and "an old row", and the first is the
 * only one worth distinguishing.
 *
 * A high rate of one value across a window is a finding about the pipeline
 * rather than about anybody's fieldwork.
 */
export const GeometrySource = z.enum(['MODEL', 'DETECTOR']);
export const GEOMETRY_SOURCE_LABELS = Object.freeze({
    MODEL: 'Dibaca model',
    DETECTOR: 'Diukur detektor',
});
/**
 * FR-05 measurement for one SKU on one photograph.
 *
 * NO TARGET HALF ANY MORE. This was Target vs Actual until scoring v4; facing,
 * sequence and row are now graded against ADMINISTRABLE ABSOLUTE BANDS, so there
 * is nothing per-SKU to compare against and `skus.target_*` was dropped with the
 * comparison. What a row is measured against is the band set, which the visit
 * score copies once rather than every line repeating it.
 */
export const PlanogramSkuScore = z.object({
    skuId: Id,
    skuName: z.string(),
    /**
     * Which division this line counts towards — stamped, not joined.
     *
     * A line belongs to exactly one of the two visit scores, and it says which so
     * the row explains itself after the SKU master moves. The same reasoning that
     * stamps `skuCategoryId` beside it.
     */
    division: SkuDivision,
    actual: z.object({
        facing: z.number().int().nonnegative(),
        sequence: z.number().int().positive().nullable(),
        row: z.number().int().positive().nullable(),
    }),
    /**
     * The four FR-05 grades for this SKU.
     *
     * `availabilityGrade` is the VISIT's grade for this line's division, copied
     * onto every line belonging to it. Visit grain since v4 — a product found on
     * any shelf in the outlet is stocked, so counting it per photograph would
     * grade the same outlet differently for taking more pictures. Copied rather
     * than joined so one line explains its own score without reaching for its
     * parent.
     */
    availabilityGrade: PlanogramGrade,
    facingGrade: PlanogramGrade,
    sequenceGrade: PlanogramGrade,
    rowGrade: PlanogramGrade,
    /**
     * How many facings the sequence grade was folded from.
     *
     * Each facing is graded by its own position and the MAJORITY wins, ties going
     * to the worse grade — so `sequenceGrade` alone cannot be reconciled against
     * `actual.sequence` by a reader. This is the number that makes it legible: at
     * 1 the grade IS the position's band, above 1 it is a fold.
     *
     * It is also the honest record of a degraded reading. Per-facing positions are
     * only recoverable from measured geometry; on a model-read photo one position
     * is graded per detection however many facings were counted, and
     * `PlanogramScore.geometrySource` says why.
     *
     * NULL WHEN NOTHING COULD BE GRADED — the model placed the product nowhere, so
     * sequence grades C by absence. Zero would read as a count.
     */
    sequenceFacingsGraded: z.number().int().positive().nullable(),
    /** `rowGrade === 'A'` — the product is on a top-band shelf. */
    rowMatch: z.boolean(),
    /**
     * 0-100 contribution of this SKU to the totals above it.
     *
     * Floors at 50, not 0 — every grade is worth at least 50 points and the four
     * weights sum to 1. See `PLANOGRAM_GRADE_POINTS`. The bound stays `min(0)` to
     * match the totals, which reach 0 when there is nothing to average; a tighter
     * bound on one and not the other invites someone to "fix" the inconsistency in
     * the wrong direction.
     */
    score: z.number().min(0).max(100),
});
/**
 * The band set a score was graded against, copied onto the result.
 *
 * Eight numbers, echoed for the reason the two availability thresholds always
 * were: a stored letter whose threshold has since moved is one nobody can
 * explain. They live on the VISIT score rather than on every line, because one
 * scoring run applies one band set to every line it writes.
 */
export const PlanogramBands = z.object({
    availabilityAMin: z.number().int().positive(),
    availabilityCMax: z.number().int().nonnegative(),
    facingAMin: z.number().int().positive(),
    facingCMax: z.number().int().nonnegative(),
    sequenceAMax: z.number().int().positive(),
    sequenceBMax: z.number().int().positive(),
    rowAMax: z.number().int().positive(),
    rowBMax: z.number().int().positive(),
});
/**
 * FR-05 planogram score for one photograph — a SUBTOTAL, not a verdict.
 *
 * DIVISION-BLIND ON PURPOSE. One photograph can hold both HSJ and LKJ product;
 * each line carries its own division's availability grade and this is the mean
 * over all of them. It answers "how well is this photograph merchandised", which
 * is the question the staff app's photo card asks and the one a supervisor asks
 * of a drill-down. The number a salesperson is measured on is
 * `PlanogramVisitScore`, and there are two of those.
 *
 * PRODUCED BY THE API, NOT BY ai-svc. The arithmetic lives in
 * `apps/api/src/modules/scoring/planogram-scoring.ts` and runs on both
 * `photo.analysed` and `photo.corrected`, so one implementation serves every
 * number that reaches a performance review. ai-svc reports what it can see and
 * scores nothing.
 */
export const PlanogramScore = z.object({
    perSku: z.array(PlanogramSkuScore),
    /**
     * The mean of `perSku` scores for this photograph.
     *
     * TWO FLOORS, AND THEY ARE DIFFERENT NUMBERS. A photo with at least one scored
     * SKU cannot fall below 50, because no grade is worth less and the weights sum
     * to 1. A photo with NO scored SKUs — a shelf the salesperson confirmed is
     * empty — has nothing to average and scores 0. That discontinuity is
     * deliberate: a total stock-out is the most severe finding the measurement can
     * make and must not be rounded up into the same band as a badly merchandised
     * shelf.
     */
    totalScore: z.number().min(0).max(100),
    /** Which formula produced this. 4 is division-split absolute bands. */
    scoringVersion: z.number().int().positive(),
    /** The band `totalScore` falls in. Never re-derived on a client. */
    rank: PlanogramRank,
    /**
     * Whether this photograph's placement was MEASURED or GENERATED — stamped so a
     * grade can be explained.
     *
     * Per-facing sequence grading needs real box positions: on `DETECTOR` geometry
     * a run of N facings provably occupies positions S..S+N-1, so each is graded
     * and the majority wins. On `MODEL` geometry `sequence` is a product ordinal
     * and expanding it would manufacture overlaps, so the single stored position is
     * graded once. Two photographs of one shelf can therefore grade differently,
     * and this is the column that says why.
     */
    geometrySource: GeometrySource,
});
/**
 * FR-05 for one division of one visit — THE verdict, and there are two per visit.
 *
 * WHY THE VISIT AND NOT THE PHOTOGRAPH. Availability asks how much of our range
 * the outlet stocks, which is a fact about the outlet and not about any one
 * picture of it. Scored per photo it punished a salesperson for photographing a
 * second fixture that happened to hold fewer products, and rewarded one who took
 * a single narrow shot. At visit grain a SKU found anywhere counts once.
 *
 * WHY TWO. HSJ and LKJ are separate sales divisions answering to separate
 * targets, and a combined mean lets a strong one mask a failing one. They are
 * never averaged together; a reader who wants one number is asking a question
 * this measurement declines to answer.
 *
 * A DIVISION WITH NO DETECTIONS SCORES 0, not 50 and not null. Its availability
 * grades C and there are no lines to average — the same rule, and the same
 * reasoning, as a shelf confirmed empty. Null would make a total stock-out
 * indistinguishable from an outlet nobody measured.
 */
export const PlanogramVisitScore = z.object({
    division: SkuDivision,
    /**
     * The mean of every scored line of this division across ALL photos of the
     * visit. Floors at 50 when there is anything to average; 0 when there is not.
     */
    totalScore: z.number().min(0).max(100),
    scoringVersion: z.number().int().positive(),
    rank: PlanogramRank,
    availability: z.object({
        /** Distinct internal SKUs of THIS division seen anywhere in the visit. */
        distinctInternalSkus: z.number().int().nonnegative(),
        grade: PlanogramGrade,
    }),
    /** How many SKU lines the mean was taken over. 0 when the division was absent. */
    scoredLines: z.number().int().nonnegative(),
    bands: PlanogramBands,
});
/** One product detected in one photo. */
export const DetectedItem = z.object({
    detectionId: Id,
    /** Null when the model saw a product it could not match to Master SKU. */
    skuId: Id.nullable(),
    skuName: z.string(),
    type: SkuType,
    /** FR-05 requires the type confidence to be surfaced, 0–100. */
    typeConfidence: Confidence,
    detectionConfidence: Confidence,
    price: PriceObservation.nullable(),
    facing: z.number().int().nonnegative(),
    sequence: z.number().int().positive().nullable(),
    row: z.number().int().positive().nullable(),
    /** Region reference retained for the corrections training dataset (TAD 4.4.3). */
    bbox: BoundingBox.nullable(),
});
/** Which layer of the FR-09 hybrid engine produced this result. */
export const InferenceTier = z.enum([
    'CLOUD', //       Gemini Flash class / Cloud Vision — cloud-first at launch (TAD 4.4.2)
    'SELF_HOSTED', // PaddleOCR tier — enabled during the operational phase
    'DEMO', //        FR-09 / NFR-06 mock fallback when external AI is unavailable
]);
/**
 * Result status.
 *
 * TSP 3.5 requires every failure path to degrade honestly and never present an
 * unresolving spinner — so "no shelf in this image" is a first-class EMPTY
 * outcome, not an error, and DEMO output is labelled as such in the UI.
 */
export const AnalysisStatus = z.enum(['OK', 'EMPTY', 'DEGRADED', 'FAILED']);
/**
 * THE contract between ai-svc, worker, api and the PWA.
 *
 * Produced by ai-svc (validated against its Pydantic mirror), persisted by the
 * worker, pushed to the client over SSE, and rendered by the FR-05 result
 * screen. Changing this shape is a cross-team event — see docs/adr/0002.
 */
export const PhotoAnalysis = z.object({
    photoId: Id,
    visitId: Id,
    photoType: PhotoType,
    status: AnalysisStatus,
    tier: InferenceTier,
    /** Whether facings, rows and sequence were measured or generated — see `GeometrySource`. */
    geometrySource: GeometrySource,
    /** Provider + model identifier, persisted on every correction for training lineage. */
    modelVersion: z.string().max(120),
    detections: z.array(DetectedItem),
    /**
     * Whether the image shows the thing it was classified as.
     *
     * NOT SHELF-SPECIFIC, despite ai-svc calling it `shelfPresent` in the Gemini
     * response schema — that is the prompt's wording, and the prompt sets it false
     * when the image shows NEITHER a shelf NOR a menu. It is a validity flag for
     * whichever `photoType` was resolved, which is why it is named for the subject
     * rather than the shelf here.
     *
     * It used to stay inside ai-svc, where it only chose between two wordings of
     * `message`. The staff app now branches on it: a photo of the ceiling must be
     * retaken and must NOT be offered a "add the products by hand" affordance,
     * while a genuine shelf the model could not read must be offered exactly that.
     * Free text cannot carry that distinction.
     */
    subjectPresent: z.boolean(),
    /**
     * Shelf levels visible in the photograph, counting from the top.
     *
     * Perception, not arithmetic — which is why it comes from the model rather
     * than from `max(detection.row)`. A shelf whose top board holds nothing our
     * catalogue knows about still has that board.
     */
    totalRows: z.number().int().positive().nullable(),
    /** Human-readable degradation reason, shown to the user when status !== OK. */
    message: z.string().nullable(),
    processedAt: Timestamp,
    /** Milliseconds from upload completion to result availability — the NFR-02 clock (TAD 4.3.1). */
    inferenceMs: z.number().int().nonnegative(),
    /**
     * How many faces the preprocessor blurred before any egress.
     *
     * On the contract rather than internal to ai-svc because it is EVIDENCE. TSP
     * 7.8 F2 treats face blur as the control the TAD's cross-border transfer
     * argument rests on, and a control you cannot evidence after the fact is not a
     * control. A row with tier CLOUD is a claim that this image left the country;
     * this number is the claim that it left with the faces gone.
     */
    facesBlurred: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// The effective view — FR-06
// ---------------------------------------------------------------------------
//
// NOTHING BELOW THIS LINE MAY ENTER `scripts/emit-json-schema.ts`.
//
// That script emits exactly three schemas — PhotoAnalysis, DetectedItem and
// PriceObservation — and `apps/ai-svc/tests/test_contract_drift.py` asserts
// field-name and required-set equality against the Pydantic mirror for each.
// `PlanogramScore` was the fourth until scoring moved into the API: ai-svc no
// longer produces it, so mirroring it in Python would assert agreement about a
// shape that service never builds. These types describe what the API serves to the staff app AFTER user
// corrections are folded over the model's reading; ai-svc has never seen a
// correction and never will. Adding `origin` to `DetectedItem` instead of
// introducing `EffectiveDetectedItem` would break the drift test and, worse,
// imply the model produces provenance it cannot know.
/**
 * Who is responsible for the row on screen.
 *
 * Three states rather than two, because "the model read this and a human
 * changed it" and "a human added this outright" are different claims. The
 * second has no model reading behind it at all, so its confidence is not a
 * confidence — see `EffectiveDetectedItem.detectionConfidence`.
 */
export const DetectionOrigin = z.enum(['AI', 'AI_CORRECTED', 'STAFF']);
/** Which fields a human overrode, so the UI can mark them individually. */
export const CorrectedField = z.enum(['SKU', 'TYPE', 'PRICE', 'FACING', 'SEQUENCE', 'ROW']);
/**
 * A `DetectedItem` as the staff app sees it after corrections are overlaid.
 *
 * WHY THIS EXISTS AT ALL: `detections` is append-only at the database level
 * (migration 0016), so a correction never rewrites what the model said — it
 * appends a `corrections` row. Without this view the read path returns the
 * model's original reading forever, and a user who corrects a SKU watches their
 * edit vanish on the next refetch.
 */
export const EffectiveDetectedItem = z.object({
    /**
     * Stable row identity: `detectionId ?? addedItemId`. This is what the client
     * echoes back when it corrects the row, and it does not change when the row is
     * edited again.
     */
    itemId: Id,
    /** Null for a row a user added — there is no detection behind it. */
    detectionId: Id.nullable(),
    /** Null for a row the model detected. The id of the ADD_ITEM correction otherwise. */
    addedItemId: Id.nullable(),
    skuId: Id.nullable(),
    skuName: z.string(),
    type: SkuType,
    /**
     * For a STAFF row these are 100, which is NOT a model confidence — nothing
     * inferred it. NFR-06 requires confidence to be displayed honestly, so the UI
     * must render the origin badge for such a row instead of a confidence pill.
     */
    typeConfidence: Confidence,
    detectionConfidence: Confidence,
    price: PriceObservation.nullable(),
    facing: z.number().int().nonnegative(),
    sequence: z.number().int().positive().nullable(),
    row: z.number().int().positive().nullable(),
    bbox: BoundingBox.nullable(),
    origin: DetectionOrigin,
    correctedFields: z.array(CorrectedField),
    /**
     * Set when `price` was copied from this outlet's last reading of this SKU
     * rather than read off this photograph. Null in every other case, including
     * the case that used to be the only one: no price at all.
     *
     * WHY IT IS NOT A FLAG ON `PriceObservation`. That type is emitted to
     * `schema/PriceObservation.json` with `additionalProperties: false` and
     * mirrored in Pydantic, and ai-svc has no concept of a carried price — it
     * reads pixels and nothing else. Putting provenance there would break the
     * drift test and claim the model produces something it cannot know, which is
     * the same argument that put `origin` on this type rather than on
     * `DetectedItem`.
     *
     * THE DATE IS THE WHOLE PAYLOAD, because it is the only thing that lets the
     * person standing at the shelf judge the number. A price from last week
     * deserves a glance; one from three months ago deserves checking against the
     * tag. A bare boolean would say "this came from somewhere else" and leave
     * them no way to decide how much to trust it.
     */
    priceCarriedFrom: z.object({ observedAt: Timestamp }).nullable(),
});
/** Queued and running photos have no reading yet; FAILED ones never will. */
export const AnalysisProcessing = z.enum(['QUEUED', 'RUNNING', 'DONE', 'FAILED']);
/**
 * What `GET /photos/:id/analysis` returns.
 *
 * Distinct from `PhotoAnalysis`, which is the ai-svc → worker contract. This is
 * the read model: the same photo after corrections, with the persisted score and
 * the two flags the UI needs to be honest about what it is showing.
 */
export const PhotoAnalysisView = z.object({
    photoId: Id,
    processing: AnalysisProcessing,
    /** Null between upload and analysis — the model has not decided yet. */
    photoType: PhotoType.nullable(),
    status: AnalysisStatus.nullable(),
    tier: InferenceTier.nullable(),
    /**
     * Whether this reading's facings, rows and sequence were measured or generated.
     *
     * SURFACED RATHER THAN LEFT IN THE DATABASE, because a column only an operator
     * with SQL access can see does not make a silent degradation observable — it
     * relocates it. Detection does not fail closed: with the toggle on and the
     * model missing, every photograph comes back complete, ordinary-looking and
     * read by the old pipeline. The settings screen tells an administrator the
     * toggle is a REQUEST; this is the field that reports what actually happened,
     * and the two together are the only honest account of it.
     *
     * Null between upload and analysis, like `tier` — nothing has read the photo
     * yet, so it can claim neither.
     */
    geometrySource: GeometrySource.nullable(),
    modelVersion: z.string().max(120).nullable(),
    message: z.string().nullable(),
    /**
     * Whether a FAILED photo is worth re-analysing — null in every other state.
     *
     * THE CLIENT CANNOT GUESS THIS AND MUST NOT. `false` is a verdict about the
     * photograph itself, a 4xx from ai-svc or a reply that does not satisfy the
     * contract, and no number of retries changes it; the user has to take a new
     * picture. `true` means the plumbing broke and the same bytes will read fine
     * once it is back. Showing "coba analisis lagi" in the first case teaches
     * people the button does not work, which is how they learn to ignore it in the
     * second — so the card forks on this rather than always offering a retry.
     *
     * Null on a FAILED photo means nobody recorded a verdict: it failed before the
     * reason was persisted, or ai-svc replied with a FAILED reading, which carries
     * its own `message` and is not a pipeline failure at all. Both read as "a retry
     * is allowed to be offered" — the endpoint refuses only an explicit `false`.
     */
    retryable: z.boolean().nullable(),
    processedAt: Timestamp.nullable(),
    inferenceMs: z.number().int().nonnegative().nullable(),
    totalScore: z.number().min(0).max(100).nullable(),
    /**
     * The band `totalScore` falls in — PHOTO grain, so a real verdict.
     *
     * Null in three different situations the screen has to tell apart: the photo
     * is not scored at all (a menu, a failed analysis), or it was scored before
     * letter grading and its number is not comparable with a new one. `totalScore`
     * distinguishes the first from the second.
     */
    rank: PlanogramRank.nullable(),
    /**
     * Whether the image shows the thing it was classified as.
     *
     * THE STAFF APP BRANCHES ON THIS. A photograph of neither a shelf nor a menu
     * must be retaken and must NOT be offered an "add the products by hand"
     * affordance — adding products to a photo of the ceiling manufactures evidence.
     * A genuine shelf the model could not read must be offered exactly that.
     *
     * Null on a photo analysed before this was recorded, which the gate reads as
     * "resolved" so history cannot retroactively block a submit.
     */
    subjectPresent: z.boolean().nullable(),
    /**
     * The salesperson confirmed this shelf is genuinely empty.
     *
     * The third answer to a zero-detection photo, beside "retake it" and "add the
     * products by hand" — and the only one that is not a recovery. It unblocks
     * submission without changing the score, which is already 0.
     */
    emptyShelfConfirmed: z.boolean(),
    /**
     * Distinct internal SKUs detected IN THIS PHOTOGRAPH.
     *
     * NO LONGER AVAILABILITY'S INPUT, though it reads like it and used to be.
     * Since v4 availability is counted per (visit, division) across every
     * photograph, so this is the per-photo tally and nothing grades on it — it is
     * kept because it is still the one number a salesperson standing at the shelf
     * can act on. The grade it used to produce is on `skuGrades[].availability`,
     * copied from the visit.
     */
    availabilityCount: z.number().int().nonnegative().nullable(),
    detections: z.array(EffectiveDetectedItem),
    /**
     * How many readings `registeredSkusOnly` held back from `detections` above.
     *
     * Always 0 when the setting is off, and 0 for a caller that asked for
     * everything — so `detections.length + hiddenDetectionCount` is the number of
     * readings this photograph actually has, whatever the filter is doing.
     *
     * TWO CALLERS NEED EXACTLY THAT SUM, WHICH IS WHY IT IS A FIELD AND NOT AN
     * INFERENCE:
     *
     *   `photoNeedsAttention` gates "Kirim data" on an empty `detections`, which
     *   means the model read a shelf and found nothing on it. Filtered, a shelf
     *   holding only unregistered competitor packs empties the array too — and the
     *   app would tell a salesperson standing in front of a full shelf that it
     *   could not be read, then ask them to confirm it was bare. The sum keeps
     *   that prompt about the photograph rather than about the setting.
     *
     *   The console prints "N objek terdeteksi" under a table whose "Deteksi"
     *   column counts detection ROWS. Those two disagreeing with nothing on screen
     *   to explain the gap is a bug this file's drill-down has already fixed once.
     *   With the sum available it says "5 dari 8" and both stay reconcilable.
     */
    hiddenDetectionCount: z.number().int().nonnegative(),
    /**
     * The four grades per scored SKU, keyed by `skuId`.
     *
     * A SEPARATE ARRAY RATHER THAN FIELDS ON `detections`, because the two are not
     * the same set: a detection may be a competitor's product or one the model
     * could not name, neither of which is scored, and a scored SKU may have been
     * merged from several detections. Joining them on the client would invent a
     * correspondence that does not exist.
     */
    skuGrades: z.array(z.object({
        skuId: Id,
        availability: PlanogramGrade,
        facing: PlanogramGrade,
        sequence: PlanogramGrade,
        row: PlanogramGrade,
        score: z.number().min(0).max(100),
    })),
    /**
     * How many corrections stand against this photo's live analysis.
     *
     * The client needs it to disable the photo-type override before the user taps
     * it: a corrected photo cannot be re-analysed, because superseding the analysis
     * would cascade into detections that `corrections.detection_id` protects with
     * ON DELETE RESTRICT.
     */
    correctionCount: z.number().int().nonnegative(),
    /**
     * True when a correction was saved but the re-score that follows it did not
     * complete. The correction is durable either way; the number on screen is the
     * previous one. Saying so beats showing a stale score as though it were fresh.
     */
    scoreStale: z.boolean(),
});
//# sourceMappingURL=vision.js.map